import React from "react";
import { Accordion, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCcVisa,
  faCcMastercard,
  faCcAmex,
} from "@fortawesome/free-brands-svg-icons";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons";
import SubtleBadge from "components/common/SubtleBadge";
import useFormattedDate from "hooks/useFormatDate";
import CopyableText from "components/common/CopyableText";

const brandIcons = {
  visa: faCcVisa,
  mastercard: faCcMastercard,
  amex: faCcAmex,
};

const PaymentMethodAccordion = ({ method, index, activeKey, onToggle }) => {
  const brand = (method?.merchant || method?.brand || "").toLowerCase();
  const icon = brandIcons[brand] || faCreditCard;
  const ownerUser = method?.owner?.user || method?.connectAccount?.user || null;
  const ownerName = [ownerUser?.firstName, ownerUser?.lastName]
    .filter(Boolean)
    .join(" ");
  const ownerEmail = ownerUser?.email || method?.connectAccount?.email || "—";
  const paymentMethodId = method?.stripePaymentMethodId || method?.externalAccountId || "—";
  const maskedCard = method?.last4 ? `••••${method.last4}` : "—";
  const isIncomingDefault = Boolean(
    method?.defaultIncomingPaymentMethod || method?.defaultPaymentAccount,
  );
  const isOutgoingDefault = Boolean(
    method?.defaultOutgoingPaymentMethod || method?.isDefaultForCurrency,
  );

  const formatExpiry = (m, y) => {
    if (!m || !y) return "—";
    const year = y < 0 ? `20${String(Math.abs(y)).slice(-2)}` : y;
    return `${String(m).padStart(2, "0")}/${year}`;
  };

  const { formatDateTime } = useFormattedDate();

  return (
    <Accordion
      activeKey={activeKey}
      onSelect={(eventKey) => onToggle(eventKey)}
      className="mb-3 custom-shadow rounded payment-accordion"
    >
      <Accordion.Item eventKey={index} className="border-0">
        {/* header */}
        <Accordion.Header className="p-0">
          <Row className="w-100 align-items-center me-0">
            {/* Brand Icon */}
            <Col xs="auto">
              <div className="p-2 bg-light rounded-circle shadow-sm">
                <FontAwesomeIcon
                  icon={icon}
                  size="lg"
                  className="text-primary"
                />
              </div>
            </Col>

            {/* Summary */}
            <Col>
              <h6 className="text-capitalize fw-semibold">
                {(method?.merchant || method?.brand || "Card")} {maskedCard}
              </h6>
              <div className="small text-muted">
                Exp: {formatExpiry(method?.expiryMonth, method?.expiryYear)}
              </div>
            </Col>

            {/* Status + Defaults */}
            <Col
              xs="auto"
              className="text-end d-flex align-items-center"
            >
              <SubtleBadge
                pill
                bg={method?.status === "ACTIVE" ? "success" : "secondary"}
              >
                {method?.status || "UNKNOWN"}
              </SubtleBadge>

              {isIncomingDefault && (
                <SubtleBadge pill bg="info" className="ms-1">
                  Incoming Default
                </SubtleBadge>
              )}

              {isOutgoingDefault && (
                <SubtleBadge pill bg="warning" className="ms-1 text-dark">
                  Outgoing Default
                </SubtleBadge>
              )}

              <span className="accordion-icon-wrapper">
                <FontAwesomeIcon icon="chevron-down" className="icon-closed" />
                <FontAwesomeIcon icon="chevron-up" className="icon-open" />
              </span>
            </Col>
          </Row>
        </Accordion.Header>

        {/* body */}
        <Accordion.Body>
          {/* Section Title */}
          <h6 className="text-muted mb-3 fw-bold">Payment method details</h6>

          <Row className="gy-3">
            <Col md={6}>
              <label className="text-xxs mb-0">Stripe Payment method ID:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                <CopyableText value={paymentMethodId} />
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Merchant:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                {method?.merchant || method?.brand || "—"}
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Card:</label>
              <h6 className="mb-0 fw-semibold text-sm">{maskedCard}</h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Expiry:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                {formatExpiry(method?.expiryMonth, method?.expiryYear)}
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Owner name:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                {ownerName || "—"}
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Owner email:</label>
              <h6 className="mb-0 fw-semibold text-sm">{ownerEmail}</h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0">Date Added:</label>
              <h6 className="mb-0 fw-semibold text-sm">
                {method?.addedAt
                  ? formatDateTime(method.addedAt)
                  : method?.createdAt
                    ? formatDateTime(method.createdAt)
                    : "—"}
              </h6>
            </Col>

            <Col md={6}>
              <label className="text-xxs mb-0 d-block">Status:</label>
              <SubtleBadge
                pill
                bg={method?.status === "ACTIVE" ? "success" : "secondary"}
              >
                {method?.status || "UNKNOWN"}
              </SubtleBadge>
            </Col>

            {/* Defaults Section */}
            {(isIncomingDefault || isOutgoingDefault) && (
              <Col md={12} className="pt-2">
                <label className="text-xxs mb-0 d-block">
                  Default Settings:
                </label>

                {isIncomingDefault && (
                  <SubtleBadge pill bg="info" className="me-2">
                    Incoming Default
                  </SubtleBadge>
                )}

                {isOutgoingDefault && (
                  <SubtleBadge pill bg="warning" className="text-dark">
                    Outgoing Default
                  </SubtleBadge>
                )}
              </Col>
            )}

          </Row>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default PaymentMethodAccordion;
